/** Admin booking status — three states only: Paid, Waiting, Cancelled. */

export const GROUP_FILTER = {
  ALL: 'all',
  WAITING: 'waiting',
  PAID: 'paid',
  THIS_MONTH: 'this_month',
};

export const PRIVATE_FILTER = {
  ALL: 'all',
  WAITING: 'waiting',
  PAID: 'paid',
};

export function simpleGroupStatus(tour) {
  if (!tour) return { key: 'waiting', label: 'Waiting', badge: 'warning', hint: 'Collect payments' };
  if (tour.departure_status === 'cancelled' || tour.admin_force_closed) {
    return { key: 'cancelled', label: 'Cancelled', badge: 'secondary', hint: 'Not running' };
  }
  if (tour.fully_paid || tour.departure_status === 'completed' || tour.admin_completed_at) {
    return { key: 'paid', label: 'Paid', badge: 'success', hint: 'All payments received' };
  }
  return { key: 'waiting', label: 'Waiting', badge: 'warning', hint: 'Deposits or balances still due' };
}

export function simplePrivateStatus(inquiryOrStatus) {
  const status = typeof inquiryOrStatus === 'string' ? inquiryOrStatus : inquiryOrStatus?.status;
  const s = (status || '').toLowerCase();
  if (s === 'verified') return { key: 'paid', label: 'Paid', badge: 'success', hint: 'Customer paid in full' };
  if (s === 'declined') return { key: 'cancelled', label: 'Cancelled', badge: 'secondary', hint: 'Inquiry closed' };
  return { key: 'waiting', label: 'Waiting', badge: 'warning', hint: 'Review, quote, or collect payment' };
}

export function groupMemberStatus(status) {
  switch (status) {
    case 'rest_paid':
      return { label: 'Paid', badge: 'success', payment: 'Complete' };
    case 'removed':
    case 'cancelled':
    case 'refunded':
      return { label: 'Cancelled', badge: 'secondary', payment: '—' };
    default:
      return { label: 'Waiting', badge: 'warning', payment: 'Payment due' };
  }
}

/** @deprecated use simpleGroupStatus */
export function groupDeparturePhase(tour) {
  const s = simpleGroupStatus(tour);
  const hints = {
    paid: 'All payments received',
    waiting: 'Collect deposits or balances',
    cancelled: 'Not running',
  };
  return { ...s, hint: hints[s.key] || '' };
}

/** @deprecated use simplePrivateStatus */
export function privateInquiryPhase(status) {
  const s = simplePrivateStatus(status);
  const hints = {
    paid: 'Customer paid in full',
    waiting: 'Review, quote, or collect payment',
    cancelled: 'Inquiry closed',
  };
  const nextSteps = {
    paid: 'Assign vendor and run the tour',
    waiting: 'Open the inquiry and take action',
    cancelled: 'No action needed',
  };
  return { ...s, hint: hints[s.key] || '', nextStep: nextSteps[s.key] || '' };
}

export function groupTourNeedsAction(tour) {
  return simpleGroupStatus(tour).key === 'waiting';
}

export function privateInquiryNeedsAction(inv) {
  return simplePrivateStatus(inv).key === 'waiting';
}

export function matchGroupFilter(tour, filter) {
  const key = simpleGroupStatus(tour).key;
  switch (filter) {
    case GROUP_FILTER.WAITING:
      return key === 'waiting';
    case GROUP_FILTER.PAID:
      return key === 'paid';
    default:
      return true;
  }
}

export function matchPrivateFilter(inv, filter) {
  const key = simplePrivateStatus(inv).key;
  switch (filter) {
    case PRIVATE_FILTER.WAITING:
      return key === 'waiting';
    case PRIVATE_FILTER.PAID:
      return key === 'paid';
    default:
      return true;
  }
}

const ORDER = { waiting: 0, paid: 1, cancelled: 2 };

export function sortGroupToursForAdmin(tours) {
  return [...tours].sort((a, b) => {
    const aKey = simpleGroupStatus(a).key;
    const bKey = simpleGroupStatus(b).key;
    if (ORDER[aKey] !== ORDER[bKey]) return ORDER[aKey] - ORDER[bKey];
    return (a.start_date || '').localeCompare(b.start_date || '');
  });
}

export function sortPrivateInquiriesForAdmin(inquiries) {
  return [...inquiries].sort((a, b) => {
    const aKey = simplePrivateStatus(a).key;
    const bKey = simplePrivateStatus(b).key;
    if (ORDER[aKey] !== ORDER[bKey]) return ORDER[aKey] - ORDER[bKey];
    const aDate = a.preferred_date || a.created_at || '';
    const bDate = b.preferred_date || b.created_at || '';
    return String(aDate).localeCompare(String(bDate));
  });
}

export function sharedRentalPhase(rental) {
  return simpleGroupStatus({
    ...rental,
    group_formed: rental.rental_formed ?? rental.group_formed,
    fully_paid: rental.fully_paid,
  });
}

export function sharedRentalNeedsAction(rental) {
  return sharedRentalPhase(rental).key === 'waiting';
}

export const SHARED_FILTER = GROUP_FILTER;

export function matchSharedFilter(rental, filter) {
  return matchGroupFilter(
    { ...rental, group_formed: rental.rental_formed ?? rental.group_formed },
    filter
  );
}

export function sortSharedRentalsForAdmin(rentals) {
  return sortGroupToursForAdmin(
    rentals.map((r) => ({ ...r, group_formed: r.rental_formed ?? r.group_formed }))
  );
}

export function groupMemberDepositCell(status) {
  if (status === 'rest_paid' || status === 'paid') return '✓';
  return '—';
}

export function groupMemberBalanceCell(status) {
  return groupMemberStatus(status).payment;
}
